import { Flame, TrendingUp, Sparkles, Beer, Leaf, Zap } from "lucide-react";

const trends = [
  {
    icon: Beer,
    title: "Low & No-Alcohol Options",
    description: "Non-alcoholic craft beers are seeing 30%+ growth. Consider adding NA taps to capture this market.",
    source: "Brewers Association",
    sourceUrl: "https://www.brewersassociation.org/insights/the-state-of-non-alc/",
    hot: true,
  },
  {
    icon: Leaf,
    title: "Sustainable Practices",
    description: "Eco-conscious consumers prefer bars with sustainable practices. Highlight your water-saving cleaning methods.",
    source: "National Restaurant Association",
    sourceUrl: "https://restaurant.org/education-and-resources/resource-library/sustainability,-health-and-variety-top-list-of-2025-macro-trends/",
    hot: false,
  },
  {
    icon: Sparkles,
    title: "Hard Seltzer Expansion",
    description: "Dedicated hard seltzer taps are becoming standard. Consider glycol line upgrades for proper serving temps.",
    source: "BeerBoard Q1 2025 Report",
    sourceUrl: "https://www.beerboard.com/blog/beerboard-releases-q1-2025-on-premise-report/",
    hot: true,
  },
  {
    icon: TrendingUp,
    title: "Local Craft Focus",
    description: "Supporting local NH breweries resonates with customers. We service all local craft installations.",
    source: "Craft Brewing Business",
    sourceUrl: "https://www.craftbrewingbusiness.com/featured/non-alcoholic-beer-is-booming-in-2025-heres-why-craft-brewers-should-pay-attention/",
    hot: false,
  },
  {
    icon: Zap,
    title: "Nitro Coffee & Beverages",
    description: "Nitro systems for coffee and cocktails are trending. Same equipment principles apply.",
    source: "Synergy Consultants",
    sourceUrl: "https://www.synergyconsultants.com/blog-posts/beyond-beer-2025-flavor-formats-that-keep-your-bar-buzzing",
    hot: true,
  },
  {
    icon: Beer,
    title: "Self-Pour Technology",
    description: "Self-pour walls are gaining popularity. We install and maintain these systems.",
    source: "Alcohol Professor",
    sourceUrl: "https://www.alcoholprofessor.com/blog-posts/blog/2016/09/13/serve-yourself-craft-beer",
    hot: false,
  },
];

const TRENDS_IMAGE = "https://images.pexels.com/photos/32239877/pexels-photo-32239877.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const marqueeItems = [
  "Non-Alcoholic Craft Beer",
  "Hard Seltzer Systems",
  "Nitro Cold Brew",
  "Self-Pour Technology",
  "Sustainable Operations",
  "Local Craft Partnerships",
  "Smart Tap Monitoring",
  "Glycol System Upgrades",
];

export default function TrendsSection() {
  return (
    <section id="trends" className="py-24 bg-[#0A0A0A]" data-testid="trends-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div
          className="text-center mb-16"
        >
          <p className="overline mb-4" data-testid="trends-overline">Industry Insights</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-4" data-testid="trends-heading">
            What's <span className="gold-text">NEW & HOT</span> in Draft
          </h2>
          <p className="text-[#A1A1AA] max-w-2xl mx-auto text-lg">
            Stay ahead of your competition with the latest trends in the draft beverage industry. 
            We keep you informed so you can make smart decisions.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-center">
          {/* Image */}
          <div
            className="relative rounded-2xl overflow-hidden h-[400px] lg:h-[500px]"
            data-testid="trends-image"
          >
            <img
              loading="lazy"
              src={TRENDS_IMAGE}
              alt="Modern bar setup"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/80 to-transparent" />
            <div className="absolute bottom-8 left-8 right-8">
              <p className="text-[#D4AF37] font-medium mb-2">2025 Trend Report</p>
              <h3 className="font-heading text-2xl text-white">
                The Future of Draft Is Here
              </h3>
            </div>
          </div>

          {/* Trends Grid */}
          <div className="grid gap-4">
            {trends.map((trend, index) => (
              <div
                key={trend.title}
                className="p-5 bg-[#141414] border border-[#27272A] rounded-xl flex items-start gap-4 card-hover"
                data-testid={`trend-card-${index}`}
              >
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                  <trend.icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-white">{trend.title}</h3>
                    {trend.hot && (
                      <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full flex items-center">
                        <Flame className="w-3 h-3 mr-1" />
                        HOT
                      </span>
                    )}
                  </div>
                  <p className="text-[#A1A1AA] text-sm">{trend.description}</p>
                  {trend.source && (
                    <a
                      href={trend.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#D4AF37] text-xs mt-1 inline-block hover:underline"
                    >
                      Source: {trend.source}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
